import { useQuery } from '@tanstack/react-query';
import { COST_CODES_QUERY } from 'apollo/queries/cost-code-queries';
import { EQUIPMENT_DISPLAY_QUERY } from 'apollo/queries/equipment-queries';
import { MEMBER_NAME_QUERY } from 'apollo/queries/member-queries';
import classNames from 'classnames';
import { projectsWithAncestorsQuery } from 'containers/manage-projects/projects-queries';
import useTimesheetsQueryParams from 'containers/timesheets/hooks/useTimesheetsQueryParams';
import useGraphQLClient from 'hooks/graphql/useGraphQLClient/useGraphQLClient';
import useReactQueryBaseKey from 'hooks/react-query/useReactQueryBaseKey/useReactQueryBaseKey';
import useEmployeeNameFormatter from 'hooks/ui/useEmployeeNameFormatter';
import useIsPrinting from 'hooks/ui/useIsPrinting';
import { DateTime } from 'luxon';
import { IMember } from 'types';
import { ClassName } from 'types/ClassName';
import ICostCode from 'types/CostCode';
import IEquipment from 'types/Equipment';
import IProject from 'types/Project';
import ITimeRange from 'types/TimeRange';
import { getFormattedPathFromProject } from 'utils/projectUtils';
import { getCostCodeDisplay, getEquipmentDisplay } from 'utils/stringUtils';
import './TimesheetPrintHeader.scss';

export interface ITimesheetPrintHeaderProps {
  className?: ClassName;
  title: string;
  timeRange: ITimeRange<DateTime>;
}

const TimesheetPrintHeader = (props: ITimesheetPrintHeaderProps) => {
  const { className, title, timeRange } = props;

  const { memberIds, projectIds, costCodeIds, equipmentIds } = useTimesheetsQueryParams();
  const isPrinting = useIsPrinting();
  const graphqlClient = useGraphQLClient();
  const baseKey = useReactQueryBaseKey();
  const nameFormatter = useEmployeeNameFormatter();

  const { data: members = [] } = useQuery({
    queryKey: [...baseKey, 'print-header-members', memberIds],
    queryFn: async () => {
      const result = await graphqlClient.request<{ members: IMember[] }>({
        document: MEMBER_NAME_QUERY,
        variables: { filter: { id: { contains: memberIds } } },
      });

      return result.members ?? [];
    },
    enabled: isPrinting && memberIds.length > 0,
  });

  const { data: projects = [] } = useQuery({
    queryKey: [...baseKey, 'print-header-projects', projectIds],
    queryFn: async () => {
      const result = await graphqlClient.request<{ projects: IProject[] }>({
        document: projectsWithAncestorsQuery,
        variables: { filter: { id: { contains: projectIds } } },
      });

      return result.projects ?? [];
    },
    enabled: isPrinting && projectIds.length > 0,
  });

  const { data: costCodes = [] } = useQuery({
    queryKey: [...baseKey, 'print-header-cost-codes', costCodeIds],
    queryFn: async () => {
      const result = await graphqlClient.request<{ costCodes: ICostCode[] }>({
        document: COST_CODES_QUERY,
        variables: { filter: { id: { contains: costCodeIds } } },
      });

      return result.costCodes ?? [];
    },
    enabled: isPrinting && costCodeIds.length > 0,
  });

  const { data: equipments = [] } = useQuery({
    queryKey: [...baseKey, 'print-header-equipment', equipmentIds],
    queryFn: async () => {
      const result = await graphqlClient.request<{ equipment: IEquipment[] }>({
        document: EQUIPMENT_DISPLAY_QUERY,
        variables: { filter: { id: { contains: equipmentIds } } },
      });

      return result.equipment ?? [];
    },
    enabled: isPrinting && equipmentIds.length > 0,
  });

  function getTimeRangeFormat() {
    const today = DateTime.local();
    const sameYears = today.hasSame(timeRange.startTime, 'year') && today.hasSame(timeRange.endTime, 'year');
    return sameYears ? 'EEE, MMM d' : 'EEE, MMM d, yyyy';
  }

  const classes = classNames('timesheet-print-header', 'print', 'mb-5', 'mt-5', className);

  return (
    <div className={classes}>
      <h2 className="mb-3">{title}</h2>
      <div>
        {timeRange.startTime.toFormat(getTimeRangeFormat()) + ' - ' + timeRange.endTime.toFormat(getTimeRangeFormat())}
      </div>
      {members.length > 0 && (
        <div>{members.map((m) => nameFormatter(m.firstName ?? '', m.lastName ?? '')).join(', ')}</div>
      )}
      {projects.length > 0 && <div>{projects.map((p) => getFormattedPathFromProject(p, true)).join(', ')}</div>}
      {costCodes.length > 0 && <div>{costCodes.map((cc) => getCostCodeDisplay(cc)).join(', ')}</div>}
      {equipments.length > 0 && <div>{equipments.map((e) => getEquipmentDisplay(e)).join(', ')}</div>}
    </div>
  );
};

export default TimesheetPrintHeader;
